<template>
  <transition name="v2026-toast-fade">
    <div v-if="open"
         class="v2026-toast"
         :class="'v2026-toast--' + variant"
         role="status"
         aria-live="polite"
         @mouseenter="onHoverIn"
         @mouseleave="onHoverOut"
         @focusin="onHoverIn"
         @focusout="onHoverOut">
      <span class="v2026-toast__icon" aria-hidden="true">
        <!-- filled circle; inherits the variant color via currentColor. Glyph follows the icon prop -->
        <svg viewBox="0 0 24 24" width="28" height="28">
          <circle cx="12" cy="12" r="12" fill="currentColor"/>
          <g v-if="icon === 'alert'">
            <path d="M12 6.25v7" fill="none" stroke="#ffffff" stroke-width="2"
                  stroke-linecap="round"/>
            <circle cx="12" cy="17.25" r="1.35" fill="#ffffff"/>
          </g>
          <path v-else d="M7 12.5l3 3 7-7" fill="none" stroke="#ffffff" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <div class="v2026-toast__text">
        <div class="v2026-toast__title">{{ title }}</div>
        <div class="v2026-toast__msg" v-if="message">{{ message }}</div>
        <!-- version2026: rendered only when actionLabel is set; absent for every pre-existing caller. -->
        <button v-if="actionLabel" type="button" class="v2026-toast__action" @click="$emit('action')">
          {{ actionLabel }}
        </button>
      </div>
      <button type="button" class="v2026-toast__close" aria-label="ปิด" @click="$emit('close')">×</button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'BaseToast',
  props: {
    // version2026: whether the toast is shown. Drives the auto-hide timer lifecycle.
    open: {
      type: Boolean,
      default: false
    },
    // version2026: theme — 'success' (teal) | 'danger' (red). Styles the bg/border/icon via CSS.
    variant: {
      type: String,
      default: 'success'
    },
    // version2026: bold headline line.
    title: {
      type: String,
      default: ''
    },
    // version2026: secondary detail line; the row is hidden when empty.
    message: {
      type: String,
      default: ''
    },
    // version2026: auto-hide delay in ms; 0 disables auto-hide (toast stays until closed).
    autoHideDelay: {
      type: Number,
      default: 2500
    },
    // version2026: optional action button (e.g. 'เลิกทำ' / 'ลองใหม่'). Empty string = no button and
    // no extra DOM, so every existing caller renders byte-identically.
    actionLabel: {
      type: String,
      default: ''
    },
    // version2026: glyph — 'check' | 'alert'. Default 'check' = the only glyph before, so existing callers are unchanged.
    icon: {
      type: String,
      default: 'check'
    }
  },
  watch: {
    // start/stop the auto-hide timer as the toast opens/closes
    open (isOpen) {
      this.syncOpenState(isOpen)
    }
  },
  mounted () {
    // honour a toast mounted already in the open state
    this.syncOpenState(this.open)
  },
  beforeDestroy () {
    // never leave a timer pending
    this.clearTimer()
  },
  methods: {
    syncOpenState (isOpen) {
      this.clearTimer()
      if (isOpen && this.autoHideDelay > 0) {
        this._hideTimer = setTimeout(() => {
          this.$emit('close')
        }, this.autoHideDelay)
      }
    },
    // version2026: only a toast that OFFERS an action can have its timer paused. Without an action
    // there is nothing to reach for, so every pre-existing caller keeps the exact old timing.
    onHoverIn () {
      if (!this.actionLabel) return
      this.clearTimer()
    },
    onHoverOut () {
      if (!this.actionLabel) return
      this.syncOpenState(this.open)
    },
    clearTimer () {
      if (this._hideTimer) {
        clearTimeout(this._hideTimer)
        this._hideTimer = null
      }
    }
  }
}
</script>

<style scoped>
.v2026-toast {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1070;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 352px;
  min-height: 68px;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}
.v2026-toast--success {
  background: #EFFAFC;
  border: 1px solid #31C1D7A3;
}
.v2026-toast--danger {
  background: #FEF2F2;
  border: 1px solid #E5484DA3;
}
.v2026-toast__icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  line-height: 0;
}
.v2026-toast--success .v2026-toast__icon {
  color: #31C1D7;
}
.v2026-toast--danger .v2026-toast__icon {
  color: #E5484D;
}
.v2026-toast__text {
  flex: 1;
  min-width: 0;
}
.v2026-toast__title {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  line-height: 1.3;
}
.v2026-toast__msg {
  font-size: 13px;
  color: #66696C;
  line-height: 1.3;
  margin-top: 2px;
}
.v2026-toast__action {
  display: inline-flex;
  align-items: center;
  height: 28px;
  margin-top: 8px;
  padding: 0 12px;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  /* Near-black, not the variant accent: #E5484D on #FEF2F2 is ~3.8:1 and fails 4.5:1 at 13px. */
  color: #1F2937;
  cursor: pointer;
}
.v2026-toast__action:hover {
  background: #F8F9FA;
}
.v2026-toast__action:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
}
.v2026-toast--success .v2026-toast__action {
  border-color: #31C1D7A3;
}
.v2026-toast--danger .v2026-toast__action {
  border-color: #E5484DA3;
}
.v2026-toast__close {
  border: none;
  background: none;
  padding: 0;
  font-size: 18px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
  align-self: flex-start;
}
.v2026-toast-fade-enter-active,
.v2026-toast-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.v2026-toast-fade-enter,
.v2026-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
